import type { VisualDocumentContent } from "~vue/appTypes";
import type { WorkDocumentReadFailure } from "~vue/ChatDetail/workDocumentTypes";
import { FILE_UPLOAD_LIMIT } from "~vue/utils/constants";
import { IMAGE_FILE_EXTENSIONS } from "~vue/utils/fileUpload";
import { computed, onScopeDispose, ref } from "vue";
import { useI18n } from "vue-i18n";

const IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp", "image/gif"];
const DOCUMENT_TYPES = ["application/pdf"];
const READER_FILE_EXTENSIONS = [".pdf", ...IMAGE_FILE_EXTENSIONS];
const OFFICE_FILE_MIME_TYPES = new Set([
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
]);
const READER_MIME_TYPES = new Set(["application/pdf", ...IMAGE_TYPES]);
// Browsers can hand drops an empty MIME type; route those by extension.
const isReaderFile = (file: File) =>
    READER_MIME_TYPES.has(file.type) || (file.type === "" && READER_FILE_EXTENSIONS.some((extension) => file.name.toLowerCase().endsWith(extension)));

const isOfficeFile = (file: File) => OFFICE_FILE_MIME_TYPES.has(file.type);

// Office files are zips: embedded images live under a media folder, and entry names
// appear uncompressed in the central directory at the end of the file, so a bounded
// tail scan finds them without a zip library.
const OFFICE_MEDIA_MARKERS = ["word/media/", "ppt/media/", "xl/media/"];
const ZIP_TAIL_BYTES = 512 * 1024;
const officeFileHasImages = async (file: File): Promise<boolean> => {
    const tail = new Uint8Array(await file.slice(Math.max(0, file.size - ZIP_TAIL_BYTES)).arrayBuffer());
    const text = new TextDecoder("latin1").decode(tail);
    return OFFICE_MEDIA_MARKERS.some((marker) => text.indexOf(marker) !== -1);
};
const countOfficeFilesWithImages = async (files: File[]): Promise<number> => {
    let count = 0;
    for (const file of files) {
        if (isOfficeFile(file) && (await officeFileHasImages(file))) {
            count += 1;
        }
    }
    return count;
};

const READER_FALLBACK_CODES = new Set(["provider_unavailable", "provider_request_rejected", "provider_response_incomplete", "read_failed", "capabilities_unavailable"]);

interface IntakeOptions {
    visualEnabled: () => boolean;
    uploadEnabled: () => boolean;
    disabled: () => boolean;
    remaining: () => number;
    legacyLimit: (file: File) => Promise<number> | number;
    upload: (files: File[], source: string, fields: Record<string, string>, isStale: () => boolean) => Promise<{ file: File }[]>;
    addVisual: (file: VisualDocumentContent) => void | Promise<void>;
    error: (message: string) => void;
    notice: (message: string) => void;
}

export function useDocumentIntake(options: IntakeOptions) {
    const { t } = useI18n();
    const isWorkDocumentDialogOpen = ref(false);
    const pendingWorkDocument = ref<File | null>(null);
    const workDocumentQueue = ref<File[]>([]);
    const readerFallbackGeneration = ref(0);
    const inFlightUploads = ref(0);
    let active = true;
    let contextGeneration = 0;
    let continueQueue = false;
    const busy = computed(() => isWorkDocumentDialogOpen.value || inFlightUploads.value > 0);
    const closeReader = () => {
        continueQueue = false;
        workDocumentQueue.value = [];
        pendingWorkDocument.value = null;
        isWorkDocumentDialogOpen.value = false;
        readerFallbackGeneration.value += 1;
    };
    onScopeDispose(() => {
        active = false;
        closeReader();
    });
    const reset = () => {
        contextGeneration += 1;
        closeReader();
    };
    const advanceOrCloseReader = () => {
        if (workDocumentQueue.value.length) {
            pendingWorkDocument.value = workDocumentQueue.value.shift() ?? null;
        } else {
            closeReader();
        }
    };
    const routeFiles = async (files: File[] | null, source: string) => {
        if (!active || !files?.length || options.disabled()) {
            return;
        }
        const openSlots = Math.max(0, options.remaining() - workDocumentQueue.value.length - (isWorkDocumentDialogOpen.value ? 1 : 0) - inFlightUploads.value);
        const accepted = files.slice(0, openSlots);
        const readerFiles = options.visualEnabled() ? accepted.filter(isReaderFile) : [];
        const otherFiles = accepted.filter((file) => !readerFiles.includes(file));
        if (readerFiles.length) {
            workDocumentQueue.value.push(...readerFiles);
            if (!isWorkDocumentDialogOpen.value) {
                pendingWorkDocument.value = workDocumentQueue.value.shift() ?? null;
                isWorkDocumentDialogOpen.value = true;
            }
        }
        const generation = contextGeneration;
        const isStale = () => !active || generation !== contextGeneration;
        if (otherFiles.length && options.uploadEnabled()) {
            inFlightUploads.value += otherFiles.length;
            try {
                const uploaded = await options.upload(otherFiles, source, {}, isStale);
                if (active && options.visualEnabled()) {
                    const count = await countOfficeFilesWithImages(uploaded.map(({ file }) => file));
                    if (count) {
                        options.notice(t("chat.composer.uploadOfficeImagesIgnored", { count }));
                    }
                }
            } finally {
                inFlightUploads.value -= otherFiles.length;
            }
        }
        if (!isStale() && files.length > accepted.length) {
            options.error(t("chat.composer.uploadSkippedAtLimit", { count: files.length - accepted.length, limit: FILE_UPLOAD_LIMIT }));
        }
    };
    const handleWorkDocumentAdded = async (file: VisualDocumentContent) => {
        if (!active || options.disabled() || options.remaining() <= 0) {
            options.error(t("chat.composer.uploadSkippedAtLimit", { count: workDocumentQueue.value.length + 1, limit: FILE_UPLOAD_LIMIT }));
            closeReader();
            return;
        }
        continueQueue = true;
        await options.addVisual(file);
    };
    const handleWorkDocumentDialogOpen = (open: boolean) => {
        if (open) {
            isWorkDocumentDialogOpen.value = true;
            return;
        }
        if (continueQueue && workDocumentQueue.value.length) {
            continueQueue = false;
            pendingWorkDocument.value = workDocumentQueue.value.shift() ?? null;
            return;
        }
        if (workDocumentQueue.value.length) {
            options.error(t("chat.composer.readQueueDiscarded", { count: workDocumentQueue.value.length }));
        }
        closeReader();
    };
    const handleWorkDocumentReadFailed = async ({ file, code, blocked }: WorkDocumentReadFailure) => {
        if (blocked || !(code === null || READER_FALLBACK_CODES.has(code)) || !options.uploadEnabled() || options.remaining() <= 0 || !DOCUMENT_TYPES.includes(file.type)) {
            return;
        }
        const generation = readerFallbackGeneration.value;
        const isStale = () => !active || generation !== readerFallbackGeneration.value;
        const limitResult = options.legacyLimit(file);
        const limit = typeof limitResult === "number" ? limitResult : await limitResult;
        if (isStale() || file.size > limit) {
            return;
        }
        const uploaded = await options.upload([file], "visual_reader_fallback", { reader_fallback: "true", reader_fallback_code: code ?? "unknown" }, isStale);
        if (isStale() || !uploaded.length) {
            return;
        }
        options.notice(t("chat.composer.visualReadFallbackNotice", { fileName: file.name }));
        advanceOrCloseReader();
    };
    return {
        routeFiles,
        busy,
        reset,
        isWorkDocumentDialogOpen,
        pendingWorkDocument,
        readerFallbackGeneration,
        handleWorkDocumentAdded,
        handleWorkDocumentDialogOpen,
        handleWorkDocumentReadFailed,
    };
}
